'use client';

import { PortableText } from '@portabletext/react';
import {
  customRichTextComponent,
  richTextComponent,
} from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { GroqImage } from '@/helpers/Image';
import type { PageAffiliateV2Schema } from '@/sanity/pages/PageAffiliateV2Template';
import Image from 'next/image';
import logo from '@/public/logo-black.svg';
import arrow from '@/public/global/dotted-arrow.svg';
import { motion } from 'framer-motion';
import { Fragment } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import bg from '@/public/global/bg-art.jpg';
import ChipSerif from '@/components/global/ui/ChipSerif';
import coopBG from '@/public/affiliate/cooperationbg.png';
import { useIsMobile } from '@/hooks/useIsMobile';
import shadow from '@/public/comp_affiliate-v2/Shadow.png';
import shadowM from '@/public/comp_affiliate-v2/ShadowM.png';
import angleLine from '@/public/affiliate-v2/angledLine.svg';
import line from '@/public/affiliate-v2/line.svg';

function DotGrid() {
  return (
    <svg
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      // viewBox="0 0 851 689"
      fill="none"
    >
      <rect
        width="100%"
        height="100%"
        rx="12"
        fill="url(#pattern0_4883_909)"
        fill-opacity="0.1"
      />
      <defs>
        <pattern
          id="pattern0_4883_909"
          patternUnits="userSpaceOnUse"
          patternTransform="matrix(12.688 0 0 12.688 0 0)"
          preserveAspectRatio="none"
          viewBox="0 0 126.88 126.88"
          width="1"
          height="1"
        >
          <g id="pattern0_4883_909_inner">
            <rect width="16" height="16" fill="black" />
          </g>
        </pattern>
      </defs>
    </svg>
  );
}

export default function Hero({
  data,
  className,
}: {
  data: PageAffiliateV2Schema;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return (
    <>
      <section
        id="hero-section"
        className={cn(
          'relative overflow-clip px-[0.65rem] lg:px-[5rem] pt-[0.65rem] lg:pt-[5rem]',
          className,
        )}
      >
        <Image
          src={shadow}
          alt=""
          className="absolute inset-0 !w-full !h-full object-cover pointer-events-none hidden lg:block mix-blend-hard-light"
        />
        <div className="absolute bottom-0 left-0 h-[15rem] w-full bg-gradient-to-t from-white to-transparent" />
        <div className="relative overflow-clip w-[min(191.25rem,100%)] mx-auto pt-[5rem] pb-[3.25rem] lg:pb-[4.5rem] border-[#0000000A] border lg:border-none rounded-[2rem] lg:rounded-none">
          <Image
            src={shadowM}
            alt=""
            className="absolute inset-0 !w-full !h-full pointer-events-none lg:hidden rounded-[2rem]"
          />
          <motion.div
            className="flex justify-center lg:justify-start items-center space-x-[1rem] mb-[7rem]"
            initial={{ opacity: 0, filter: 'blur(10px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{
              duration: 0.3,
              delay: 0,
              ease: [0.25, 0.4, 0.25, 1],
            }}
          >
            <Link href={'/'}>
              <Image src={logo} alt="" className="w-[16.25rem]" />
            </Link>

            {data?.heroPartner && (
              <Fragment>
                <Image src={arrow} alt="" className="shrink-0" />
                <div className="flex items-center gap-[1rem]">
                  <p className="text-[#103037] text-[2.5rem] font-[500] leading-[116%] m-0">
                    {data?.heroPartner.text}
                  </p>
                </div>
              </Fragment>
            )}
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-[69.875rem_auto] lg:items-center gap-[4rem] lg:gap-[10.5rem] relative z-10 px-[1.5rem] lg:min-h-[96.5rem]">
            <div className="flex flex-col items-center lg:items-start w-[min(166.25rem,100%)] mt-[2rem] lg:mt-0">
              <motion.div
                className="w-[min(64.75rem,100%)] space-y-[1.5rem] mb-[4.5rem] lg:mb-0"
                initial={{ opacity: 0, x: -50, filter: 'blur(10px)' }}
                animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                transition={{
                  duration: 0.3,
                  delay: 0.05,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
              >
                <div className="bg-[#10303708] border-[#00000014] border rounded-full py-[0.75rem] px-[2rem] lg:px-[2.5rem] w-fit text-[1.5rem] lg:text-[1.75rem] font-medium uppercase">
                  {data?.heroChip1.text}
                </div>

                <div>
                  {data?.heroText && (
                    <div className="richtext richtext-start prose-h1:text-[#2E3436] prose-h1:text-[5rem] lg:prose-h1:text-[6rem] prose-h1:leading-[110%] mb-[2.25rem] lg:mb-[1.5rem]">
                      <PortableText
                        value={data.heroText}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {data?.heroContent && (
                    <div
                      className="richtext richtext-center richtext-start prose-p:text-[2rem] prose-p:leading-[150%] prose-p:font-[400]
                      prose-strong:font-[500] prose-strong:text-[#103037] prose-strong:text-[2.25rem] prose-p:text-[#1C1F22]/70"
                    >
                      <PortableText
                        value={data.heroContent}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {data?.heroItems && (
                    <div className="flex flex-col gap-[1.375rem] my-[5rem]">
                      {data.heroItems.map((item, index) => (
                        <Fragment key={'fheroItem' + index}>
                          <div
                            key={'heroItem' + index}
                            className="flex gap-[1.5rem] items-center"
                          >
                            <Image
                              src={item.icon}
                              alt="icon"
                              className="!w-[2.375rem] shrink-0"
                            />
                            <span>{item.text}</span>
                          </div>
                          {index !== data.heroItems.length - 1 && (
                            <hr className="w-full border-t border-black/10" />
                          )}
                        </Fragment>
                      ))}
                    </div>
                  )}
                </div>

                <div className="w-full">
                  <CTAButton data={data.heroCTA} className="w-full" />
                </div>
              </motion.div>
            </div>
            <div className="relative bg-[#F7F7F7] rounded-[1.5rem] w-full h-[45rem] lg:h-full">
              <div className="absolute inset-0 w-full h-full p-[2.25rem]">
                <DotGrid />
              </div>
              {data?.heroImage && (
                <motion.div
                  initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  transition={{
                    duration: 0.3,
                    delay: 0.2,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  className="absolute top-[3.25rem] left-[2.25rem] lg:top-[11.25rem] lg:left-[12.25rem] w-[30rem] h-[23.5rem] lg:w-[60rem] lg:h-[46.875rem] overflow-hidden rounded-[2rem]"
                >
                  <Image
                    src={data?.heroImage}
                    alt=""
                    quality={100}
                    className="object-center !w-full rounded-[1.5rem] shadow-[-6.764px_14.669px_37.805px_0_rgba(56,_56,_56,_0.20)]
"
                  />
                </motion.div>
              )}

              {data.heroImageBadge1 && (
                <motion.div
                  initial={{ opacity: 0, x: -120, filter: 'blur(10px)' }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    filter: 'blur(0px)',
                  }}
                  transition={{
                    duration: 0.3,
                    delay: 0.35,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  className="absolute bottom-[4.75rem] right-[1.5rem] lg:bottom-[11.75rem] lg:right-[8.5rem] w-[20rem] lg:w-[39.5rem] h-[17.875rem] lg:h-[35.75rem] shadow-[-6.764px_14.669px_37.805px_0_rgba(56,_56,_56,_0.20)]"
                >
                  <Image
                    src={data.heroImageBadge1}
                    alt=""
                    quality={100}
                    className="!w-full !h-full object-cover rounded-[0.75rem]"
                  />
                </motion.div>
              )}

              {data.heroIcon && (
                <Image
                  src={data.heroIcon}
                  alt="icon"
                  className="!w-[6.125rem] absolute top-[5.25rem] right-[9.36rem] lg:top-[14.25rem] lg:right-[26.36rem]"
                />
              )}

              {data.heroTextBadge1 && (
                <div className="absolute top-[16.25rem] right-[8.5rem] lg:top-[27.25rem] lg:right-[18.5rem] flex flex-col">
                  <div className="absolute -translate-y-full top-0 left-0 lg:translate-x-[25%] shrink-0 w-max bg-[#6DA991] py-[1rem] px-[1.5rem] rounded-[0.5rem] leading-none text-[1.5rem] lg:text-[2rem] text-[#FBFBFB] font-medium shadow-[-6.764px_14.669px_37.805px_0_rgba(56,_56,_56,_0.20)]">
                    VIP Deal - bis zu 80%
                  </div>
                  <Image
                    src={angleLine}
                    alt="angleLine"
                    className="!w-[10.625rem] lg:!w-[17.625rem]"
                  />
                </div>
              )}

              {data.heroTextBadge2 && (
                <div className="absolute bottom-[6.5rem] right-[21rem] lg:bottom-[16.5rem] lg:right-[46rem] flex">
                  <div className="w-fit lg:w-max bg-white border border-[#D5D5D5] py-[1rem] px-[1.5rem] rounded-[0.5rem] leading-none text-[1.5rem] lg:text-[2rem] text-[#707070] font-medium shadow-[-6.764px_14.669px_37.805px_0_rgba(56,_56,_56,_0.20)]">
                    {data.heroTextBadge2}
                  </div>
                  <Image
                    src={line}
                    alt="line"
                    className="!w-[5rem] lg:!w-[10rem] shrink-0"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
